// Import Firebase types
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';

// Define Firestore types
type Firestore = firebase.firestore.Firestore;
type Timestamp = firebase.firestore.Timestamp;
type FieldValue = firebase.firestore.FieldValue;
type DocumentReference = firebase.firestore.DocumentReference;
type CollectionReference = firebase.firestore.CollectionReference;
type DocumentData = firebase.firestore.DocumentData;

// Define interfaces for our data models
export interface VendorScoreMetadata extends DocumentData {
  addedDate: Timestamp | FieldValue;
  currentScore: number;
  domain: string;
  firstCaptureDate: Timestamp | FieldValue;
  latestCaptureDate: Timestamp | FieldValue;
  vendor?: string;
}

export interface VendorScoreHistory extends DocumentData {
  captureDate: Timestamp | FieldValue;
  capturedScore: number;
  domain: string;
  previousCaptureDate: Timestamp | FieldValue;
}

class VendorScoreService {
  private readonly PUBLIC_DATA_COLLECTION = 'publicData';
  private readonly VENDORS_DOC = 'vendors';
  private readonly SCORE_HISTORY_SUBCOLLECTION = 'upguard';

  private getFirestore(): Firestore {
    return firebase.firestore();
  }

  private getVendorDocRef(domain: string): DocumentReference {
    // Get a reference to the domain document directly under vendors
    return this.getFirestore()
      .collection(this.PUBLIC_DATA_COLLECTION)
      .doc(this.VENDORS_DOC);
  }

  private getVendorDomainDocRef(domain: string): DocumentReference {
    // Get a reference to the domain document directly under vendors
    return this.getFirestore()
      .collection(this.PUBLIC_DATA_COLLECTION)
      .doc(this.VENDORS_DOC)
      .collection(domain)
      .doc('scoreHistory');
  }

  private getScoreHistoryRef(domain: string): CollectionReference {
    // Get a reference to the upguard subcollection under the domain document
    return this.getVendorDomainDocRef(domain)
      .collection(this.SCORE_HISTORY_SUBCOLLECTION);
  }

  private normalizeDomain(domain: string): string {
    if (!domain) return '';
    
    try {
      // Try to parse as URL first to handle various formats
      let url: URL;
      try {
        // If it doesn't start with http, add it to make it a valid URL
        const urlStr = domain.startsWith('http') ? domain : `http://${domain}`;
        url = new URL(urlStr);
      } catch (e) {
        console.warn(`[VendorScore] Invalid URL format for domain: ${domain}`, e);
        // Fallback to simple normalization if URL parsing fails
        return domain
          .replace(/^https?:\/\//, '')
          .replace(/^www\./, '')
          .split('/')[0]
          .toLowerCase()
          .trim();
      }
      
      // Extract hostname and remove www. if present
      const normalized = url.hostname.replace(/^www\./, '').toLowerCase();
      console.log(`[VendorScore] Normalized domain: ${domain} -> ${normalized}`);
      return normalized;
    } catch (e) {
      console.error(`[VendorScore] Error normalizing domain: ${domain}`, e);
      return '';
    }
  }

  /**
   * Get the current vendor score from Firestore if it's recent enough
   * @param domain The domain to check (can be full URL or just domain)
   * @param maxAgeMs Maximum age of the score in milliseconds (default 7 days)
   * @returns The score if found and recent enough, null otherwise
   */
  async getVendorScore(
    domain: string,
    maxAgeMs: number = 7 * 24 * 60 * 60 * 1000,
    bypassCache?: boolean
  ): Promise<{ score: number; cached: true; history?: Array<{ date: Date; score: number }> } | null> {
    try {
      console.log('[VendorScore] Starting getVendorScore for domain:', domain);
      
      // Normalize the domain
      const normalizedDomain = this.normalizeDomain(domain);
      console.log(`[VendorScore] Normalized domain: ${normalizedDomain}`);

      if (bypassCache) {
        console.log('[VendorScore] Bypassing cache for domain:', normalizedDomain);
        return null;
      }

      // Get Firestore instance
      const db = firebase.firestore();
      
      // Try to get the vendors document
      const vendorsDoc = await db.collection('publicData').doc('vendors').get();
      
      if (!vendorsDoc.exists) {
        console.log('[VendorScore] No vendors document found in publicData/vendors');
        return null;
      }
      
      console.log('[VendorScore] Vendors document data:', vendorsDoc.data());
      
      // Try to access the domain as a subcollection
      try {
        const domainPath = `publicData/vendors/${normalizedDomain}`;
        const domainDoc = await db.doc(`${domainPath}/metadata`).get();
        
        if (domainDoc.exists) {
          const data = domainDoc.data() as VendorScoreMetadata;
          console.log(`[VendorScore] Found vendor metadata for ${normalizedDomain}:`, data);
          
          if (data.currentScore) {
            console.log(`[VendorScore] Using score for ${normalizedDomain}: ${data.currentScore}`);
            
            // Fetch historical data from upguard subcollection
            let history: Array<{ date: Date; score: number }> = [];
            try {
              // Debug: Log the full Firestore path we're trying to access
              const basePath = `publicData/vendors/${normalizedDomain}/scoreHistory/upguard`;
              console.log(`[VendorScore] Attempting to access: ${basePath}`);
              
              // Try accessing the upguard subcollection directly
              const upguardRef = db.collection(basePath);
                
              console.log(`[VendorScore] Upguard ref path: ${upguardRef.path}`);
              
              // Get a small sample first to test access
              const testQuery = upguardRef.limit(5);
              console.log(`[VendorScore] Testing query on: upguard subcollection`);
              const testSnapshot = await testQuery.get();
              
              if (!testSnapshot.empty) {
                console.log(`[VendorScore] Successfully retrieved ${testSnapshot.size} test documents`);
                testSnapshot.docs.forEach((doc, index) => {
                  console.log(`[VendorScore] Test doc ${index + 1}:`, doc.id, doc.data());
                });
              } else {
                console.log('[VendorScore] No documents found in test query');
              }
              
              // Now try the actual query
              const upguardQuery = upguardRef
                .orderBy('date', 'desc')
                .limit(30);
                
              console.log(`[VendorScore] Executing main query...`);
              const upguardSnapshot = await upguardQuery.get();
              console.log(`[VendorScore] Main query returned ${upguardSnapshot.size} documents`);
              
              if (!upguardSnapshot.empty) {
                history = upguardSnapshot.docs.map(doc => {
                  const docData = doc.data();
                  console.log(`[VendorScore] Raw upguard record ${doc.id}:`, docData);
                  return {
                    date: docData.date?.toDate() || new Date(),
                    score: docData.score || 0
                  };
                });
                console.log(`[VendorScore] Fetched ${history.length} historical records for ${normalizedDomain}`);
              } else {
                console.log(`[VendorScore] No historical data found in upguard subcollection for ${normalizedDomain}`);
                
                // Debug: Try to list all documents in the domain collection
                try {
                  const domainDocs = await db.collection('publicData')
                    .doc('vendors')
                    .collection(normalizedDomain)
                    .get();
                  
                  console.log(`[VendorScore] Documents in ${normalizedDomain} collection:`);
                  domainDocs.forEach(doc => {
                    console.log(`[VendorScore] Document ${doc.id}:`, doc.data());
                  });
                  
                  // Debug: List all documents in the domain collection
                  try {
                    const domainRef = db.collection('publicData')
                      .doc('vendors')
                      .collection(normalizedDomain);
                    
                    // Get all documents in the domain collection
                    const domainSnapshot = await domainRef.limit(10).get();
                    console.log(`[VendorScore] Found ${domainSnapshot.size} documents in ${normalizedDomain} collection`);
                    
                    if (!domainSnapshot.empty) {
                      domainSnapshot.docs.forEach(doc => {
                        console.log(`[VendorScore] Document ${doc.id}:`, doc.exists ? 'exists' : 'does not exist');
                      });
                    }
                    
                    // Check the scoreHistory document exists
                    const scoreHistoryRef = domainRef.doc('scoreHistory');
                    const scoreHistoryDoc = await scoreHistoryRef.get();
                    
                    if (!scoreHistoryDoc.exists) {
                      console.log('[VendorScore] scoreHistory document does not exist');
                      return;
                    }
                    
                    console.log('[VendorScore] scoreHistory document exists');
                    
                    // Try to access the upguard subcollection directly
                    try {
                      const upguardRef = scoreHistoryRef.collection('upguard');
                      console.log(`[VendorScore] Attempting to access upguard subcollection at: ${upguardRef.path}`);
                      
                      // Try to get a few documents to test access
                      const testDocs = await upguardRef.limit(3).get();
                      
                      if (!testDocs.empty) {
                        console.log(`[VendorScore] Successfully retrieved ${testDocs.size} documents from upguard subcollection`);
                        testDocs.docs.forEach((doc, index) => {
                          console.log(`[VendorScore] Upguard doc ${index + 1}:`, doc.id, doc.data());
                        });
                      } else {
                        console.log('[VendorScore] No documents found in upguard subcollection');
                      }
                    } catch (upguardError) {
                      console.error('[VendorScore] Error accessing upguard subcollection:', upguardError);
                    }
                  } catch (error) {
                    console.error('[VendorScore] Error listing subcollections:', error);
                  }
                    
                } catch (listError) {
                  console.error('[VendorScore] Error listing documents:', listError);
                }
              }
            } catch (historyError) {
              console.error(`[VendorScore] Error fetching historical data for ${normalizedDomain}:`, historyError);
            }
            
            return { 
              score: data.currentScore, 
              cached: true,
              history
            };
          }
        } else {
          console.log(`[VendorScore] No metadata found for domain: ${normalizedDomain}`);
          
          // For debugging, list all documents in the domain collection
          try {
            const domainDocs = await db.collection(domainPath).get();
            console.log(`[VendorScore] Found ${domainDocs.size} documents in ${normalizedDomain} collection:`);
            
            domainDocs.forEach(doc => {
              console.log(`[VendorScore] Document ${doc.id}:`, doc.data());
            });
            
            // Check for upguard subcollection
            try {
              const upguardDocs = await db.collection(`${domainPath}/scoreHistory/upguard`).get();
              console.log(`[VendorScore] Found ${upguardDocs.size} upguard records for ${normalizedDomain}`);
              
              if (!upguardDocs.empty) {
                upguardDocs.forEach(doc => {
                  console.log(`[VendorScore] Upguard record ${doc.id}:`, doc.data());
                });
              }
            } catch (upguardError) {
              console.error(`[VendorScore] Error fetching upguard data for ${normalizedDomain}:`, upguardError);
            }
            
          } catch (listError) {
            console.error(`[VendorScore] Error listing documents in ${normalizedDomain}:`, listError);
          }
        }
      } catch (error) {
        console.error(`[VendorScore] Error accessing domain ${normalizedDomain}:`, error);
      }
      
      return null;
    } catch (error) {
      console.error(`[VendorScore] Error in getVendorScore for ${domain}:`, error);
      return null;
    }
  }

  /**
   * Get the score history for a domain
   * @param domain The domain to get history for
   * @param limit Maximum number of history items to return (default 30)
   */
  async getVendorScoreHistory(
    domain: string,
    limit: number = 30
  ): Promise<VendorScoreHistory[]> {
    try {
      const normalizedDomain = this.normalizeDomain(domain);
      console.log(`[VendorScore] Getting score history for domain: ${normalizedDomain}`);
      
      // Get reference to the upguard subcollection
      const historyRef = this.getScoreHistoryRef(normalizedDomain);
      console.log(`[VendorScore] History collection path: ${historyRef.path}`);
      
      // First, try a test query to verify access
      const testQuery = historyRef.limit(1);
      const testSnapshot = await testQuery.get();
      console.log(`[VendorScore] Test query returned ${testSnapshot.size} documents`);
      
      if (testSnapshot.empty) {
        console.warn(`[VendorScore] No documents found in test query for ${normalizedDomain}`);
        return [];
      }
      
      // Now do the actual query with ordering and limit
      const query = historyRef
        .orderBy('captureDate', 'desc')
        .limit(limit);
      
      console.log(`[VendorScore] Executing main query with limit ${limit}`);
      const snapshot = await query.get();
      
      if (snapshot.empty) {
        console.warn(`[VendorScore] No documents found in main query for ${normalizedDomain}`);
        return [];
      }
      
      console.log(`[VendorScore] Main query returned ${snapshot.size} documents`);
      
      // Process and return the documents
      return snapshot.docs.map(doc => {
        const data = doc.data();
        const historyItem = {
          captureDate: data.captureDate,
          capturedScore: data.capturedScore,
          domain: data.domain || normalizedDomain,
          previousCaptureDate: data.previousCaptureDate,
          _raw: { id: doc.id, ...data } // Include raw data for debugging
        };
        
        console.log(`[VendorScore] Processed history item:`, {
          id: doc.id,
          captureDate: data.captureDate?.toDate?.() || 'No date',
          score: data.capturedScore,
          domain: data.domain || normalizedDomain
        });
        
        return historyItem as VendorScoreHistory;
      });
    } catch (error) {
      console.error(`[VendorScore] Error getting score history for domain ${domain}:`, error);
      // Log additional debug info
      console.error('[VendorScore] Error details:', {
        domain,
        errorMessage: error.message,
        errorStack: error.stack
      });
      throw error;
    }
  }

  /**
   * Update the vendor score in Firestore
   * @param domain The domain to update
   * @param score The new score
   * @param vendor The vendor name
   */
  async updateVendorScore(domain: string, score: number, vendor: string = 'upguard'): Promise<void> {
    const normalizedDomain = this.normalizeDomain(domain);
    const now = firebase.firestore.FieldValue.serverTimestamp();
    const nowDate = new Date();
    
    try {
      // Get reference to the vendor document
      const docRef = this.getVendorDocRef(normalizedDomain);
      const doc = await docRef.get();
      
      // Get the previous capture date before we update it
      const previousData = doc.exists ? (doc.data() as VendorScoreMetadata) : null;
      const previousCaptureDate = previousData?.latestCaptureDate || null;
      
      // Prepare metadata update
      const metadataUpdate: Partial<VendorScoreMetadata> = {
        currentScore: score,
        latestCaptureDate: now as unknown as Date, // Will be converted to Timestamp by Firestore
        domain: domain, // Store the original domain with protocol
        vendor,
        updatedAt: now as unknown as Date
      };
      
      // Set firstCaptureDate only if this is a new record
      if (!doc.exists) {
        metadataUpdate.firstCaptureDate = now as unknown as Date;
        metadataUpdate.addedDate = now as unknown as Date;
        console.log(`[VendorScore] Creating new vendor score for domain: ${normalizedDomain}`, metadataUpdate);
      } else {
        console.log(`[VendorScore] Updating vendor score for domain: ${normalizedDomain}`, metadataUpdate);
      }
      
      // Batch the writes for atomic updates
      const batch = this.db.batch();
      
      // Update or create the metadata document
      if (!doc.exists) {
        batch.set(docRef, metadataUpdate);
      } else {
        batch.update(docRef, metadataUpdate);
      }
      
      // Add to history - using the vendor-specific subcollection
      // Format: "Day, DD Mon, HH:MM:SS am/pm-{randomId}"
      const formattedDate = nowDate.toLocaleString('en-US', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).replace(/,/g, '').replace(/\s+/g, ' ').trim();
      
      // Generate a random ID similar to what we see in the logs
      const randomId = Math.random().toString(36).substring(2, 15) + 
                      Math.random().toString(36).substring(2, 15);
      
      const historyDocRef = this.getScoreHistoryRef(normalizedDomain)
        .doc(`${formattedDate}-${randomId}`);
      
      const historyData = {
        captureDate: now as unknown as Date,
        capturedScore: score,
        domain: domain, // Original domain with protocol
        previousCaptureDate: previousCaptureDate ? previousCaptureDate : null,
        vendor,
        createdAt: now as unknown as Date
      };
      
      batch.set(historyDocRef, historyData);
      
      // Commit the batch
      await batch.commit();
      
      console.log(`[VendorScore] Successfully updated vendor score for domain: ${normalizedDomain}`, {
        score,
        previousScore: previousData?.currentScore,
        previousCapture: previousCaptureDate
      });
      
    } catch (error) {
      console.error(`[VendorScore] Error updating vendor score for domain ${domain}:`, error);
      throw error;
    }
  }
}

export const vendorScoreService = new VendorScoreService();
